/**
 * Экран «Мой график» — личные смены сотрудника (staff-portal).
 *
 * Отдельный экран с кнопкой «‹ Назад» (feedback_every_form_is_a_separate_screen):
 * грузит GET /v1/me/shifts, группирует смены по дням и показывает карточки
 * (дата · время начала-конца · статус). Доступен staff-ролям (housekeeper /
 * front_desk / restaurant / maintenance) и менеджеру/владельцу (у которых
 * тоже может быть staff_member). Пустой список — корректный empty-state
 * «Смены не назначены», НЕ ошибка.
 *
 * Бэк отдаёт ТОЛЬКО свои смены (резолв staff_member_id из JWT), чужие смены
 * сюда не попадают (RBAC).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Skeleton } from '@hsc/ui';
import type { Shift } from '@hsc/types';
import type { ModuleProps } from '../../routes/Workspace.js';
import { useMyShifts } from './useMyShifts.js';

const SHIFT_STATUS_LABELS: Readonly<Record<string, string>> = {
  scheduled: 'Запланирована',
  confirmed: 'Подтверждена',
  in_progress: 'Идёт',
  completed: 'Завершена',
  missed: 'Пропущена',
  swapped: 'Передана',
  cancelled: 'Отменена',
};

/** Тон чипа статуса смены. */
function statusTone(status: string): string {
  switch (status) {
    case 'completed':
      return 'done';
    case 'in_progress':
      return 'active';
    case 'missed':
    case 'cancelled':
      return 'alert';
    case 'swapped':
      return 'muted';
    default:
      return 'pending';
  }
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  });
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function hhmm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

interface DayGroup {
  key: string;
  label: string;
  ts: number;
  shifts: Shift[];
}

/** Группирует смены по календарному дню, дни — по возрастанию даты. */
function groupByDay(shifts: Shift[]): DayGroup[] {
  const groups = new Map<string, DayGroup>();
  for (const s of shifts) {
    const k = dayKey(s.scheduled_start);
    const existing = groups.get(k);
    if (existing) {
      existing.shifts.push(s);
    } else {
      const d = new Date(s.scheduled_start);
      groups.set(k, {
        key: k,
        label: fmtDate(s.scheduled_start),
        ts: Number.isNaN(d.getTime()) ? 0 : d.getTime(),
        shifts: [s],
      });
    }
  }
  const out = Array.from(groups.values());
  out.sort((a, b) => a.ts - b.ts);
  for (const g of out) {
    g.shifts.sort(
      (a, b) =>
        new Date(a.scheduled_start).getTime() - new Date(b.scheduled_start).getTime(),
    );
  }
  return out;
}

export function MyShiftsPage(_props: ModuleProps): JSX.Element {
  const navigate = useNavigate();
  const { shifts, isLoading, isError } = useMyShifts();
  const groups = useMemo(() => groupByDay(shifts), [shifts]);

  return (
    <section data-testid="my-shifts-page">
      <button
        type="button"
        className="ta-back-btn"
        data-testid="my-shifts-back"
        onClick={() => navigate('/')}
      >
        <span className="ta-back-btn__chev" aria-hidden="true">
          ‹
        </span>{' '}
        Назад
      </button>
      <div className="ta-topbar">
        <div>
          <div className="ta-crumbs">Рабочее место / Мой график</div>
          <h1 className="hsc-page__title">Мой график</h1>
          <p className="ta-muted" data-testid="my-shifts-summary">
            Ваши назначенные смены · всего: {shifts.length}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div data-testid="my-shifts-loading">
          <Skeleton height={180} radius={16} />
        </div>
      ) : isError ? (
        <Card>
          <p role="alert" data-testid="my-shifts-error">
            Не удалось загрузить ваш график. Попробуйте обновить страницу.
          </p>
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <p data-testid="my-shifts-empty">
            Смены не назначены. Когда управляющий поставит вас в график, смены появятся здесь.
          </p>
        </Card>
      ) : (
        <div className="hsc-stack" data-testid="my-shifts-list">
          {groups.map((g) => (
            <Card key={g.key}>
              <h2 className="hsc-card__title" data-testid={`my-shifts-day-${g.key}`}>
                {g.label}
              </h2>
              <ul className="ta-feed">
                {g.shifts.map((s) => (
                  <li
                    key={s.id}
                    className="ta-feed__item"
                    data-testid={`my-shift-${s.id}`}
                  >
                    <div className="ta-feed__main">
                      <span className="ta-feed__title">
                        {hhmm(s.scheduled_start)}–{hhmm(s.scheduled_end)}
                      </span>
                      {s.notes ? (
                        <span className="ta-feed__meta">{s.notes}</span>
                      ) : null}
                    </div>
                    <div className="ta-feed__side">
                      <span className={`ta-chip ta-chip--${statusTone(s.status)}`}>
                        {SHIFT_STATUS_LABELS[s.status] ?? s.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
